import { useCallback } from "react";
import { ArrowLeft } from "lucide-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { safeInternalPath } from "@/hooks/useUrlState";

/**
 * Shared "Back" resolution (2026-09, operational-experience release) — ONE
 * reusable hook for every route-level detail page, generalizing the
 * pattern Customer 360 established (P4 Problem 10) rather than letting
 * each new detail page (Knowledge Base, Process/Case, ...) invent its own.
 *
 * Resolution order (never anything else):
 *   1. a validated internal `return_to` query param — rejected outright by
 *      safeInternalPath if it isn't a same-origin, absolute-path string
 *      (never an open redirect to an external URL);
 *   2. genuine browser/app history (`location.key !== "default"` means
 *      react-router actually pushed an entry to reach this page — i.e. the
 *      user navigated here from inside the app), via `navigate(-1)`;
 *   3. `fallbackRoute` — the safe, module-specific list page. This is what
 *      fires for a directly-opened URL, a new-tab open, or a bookmark/
 *      deep-link with no `return_to` and no history — never login, never
 *      an unrelated module, never an invalid external previous page.
 *
 * `fallbackRoute` is required and must be a same-app route string (e.g.
 * "/knowledge-base", "/process") — the caller decides it, this hook never
 * guesses one.
 */
export function useBackNavigation(fallbackRoute) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const rawReturnTo = searchParams.get("return_to");
  const returnTo = safeInternalPath(rawReturnTo ? decodeURIComponent(rawReturnTo) : null);

  return useCallback(() => {
    if (returnTo) { navigate(returnTo); return; }
    if (location.key && location.key !== "default") { navigate(-1); return; }
    navigate(fallbackRoute);
  }, [returnTo, location.key, navigate, fallbackRoute]);
}

/**
 * Build a `return_to` value from a list page's OWN current location
 * (pathname + search) for embedding in a link INTO a detail page — pair
 * with useUrlState on the list page so its tab/filter/sort/page/scope
 * state already lives in that search string before it's captured here.
 * Always run the result through encodeURIComponent-safe usage sites
 * (template-literal query-string construction), never string-concatenated
 * unescaped into an href.
 */
export function buildReturnTo(location) {
  return encodeURIComponent(location.pathname + location.search);
}

/**
 * A visible, consistent Back control — same icon/label/placement/test-id
 * pattern everywhere it's used, so it reads as one application feature
 * rather than several ad-hoc buttons. Only for route-level DETAIL pages;
 * never add this to a top-level dashboard/list page (spec: "Do not add
 * meaningless Back buttons to top-level dashboard/list pages").
 */
export function BackButton({ fallbackRoute, testId = "back-button", label = "Back", className = "" }) {
  const goBack = useBackNavigation(fallbackRoute);
  return (
    <Button variant="ghost" size="sm" className={`text-xs text-muted-foreground ${className}`}
      data-testid={testId} onClick={goBack}>
      <ArrowLeft className="h-3.5 w-3.5 mr-1" /> {label}
    </Button>
  );
}
